/**
 * The import contract — `KLQ-20260824-002`, the read half of the bridge.
 *
 * `clipboard.ts` turns a Sheets block into a grid of strings and stops there, deliberately.
 * This module is the next question and the last one the *mechanism* is allowed to ask: **what
 * does a row mean here?** It answers it by not answering it — the surface does, through an
 * `ImportAdapter`, and everything above stays domain-blind.
 *
 * THREE RULES, EACH OF THEM THE REASON THIS IS A CONTRACT AND NOT A FUNCTION
 *
 * 1. **The mechanism never decides what a row means.** A table hands the parsed grid to its
 *    adapter and renders whatever verdicts come back. A table with no adapter says so out loud
 *    rather than swallowing the paste (`KLQ-20260824-002`'s last acceptance criterion) — the
 *    silence was the defect the 2026-08-28 note names.
 * 2. **Nothing is written before a person has read the verdicts.** `preview` is a dry run and
 *    it is not optional: a wrong auto-map with a visible preview is annoying, and a wrong
 *    auto-map without one is data corruption. The two halves are separate functions so that a
 *    surface cannot accidentally write from the code path that was only supposed to look.
 * 3. **The verdict is per row, always.** One bad date does not refuse a sheet, and one row the
 *    server declines does not take the other 199 with it. That is the same skip-and-report
 *    discipline the bulk endpoints already impose, arriving one layer up.
 *
 * **Auto-matching is deliberately conservative.** Header synonyms are exact matches against a
 * normalised form — case folded, accents stripped, punctuation dropped — and never a substring
 * or a fuzzy distance. `termín` finds *due*; `termín dokončenia` does not, and the person
 * remaps it in one click. A greedy matcher would map a column nobody meant it to and the
 * preview would look convincing while being wrong about which column is which.
 */

/** One thing a surface can be told about a row, and the words a sheet might use for it. */
export interface ImportField {
  readonly key: string;
  /** The field's name in this person's language. Rendered in the mapping control. */
  readonly label: string;
  /**
   * Header words that auto-map onto this field, sk/en/de, already normalised the way
   * `normalizeHeader` normalises what a sheet actually carries. Exact matches only.
   */
  readonly synonyms: readonly string[];
  /** Without a column feeding it, no row can be understood. Drives the drawer's refusal. */
  readonly required?: boolean;
}

/**
 * Which sheet column feeds which field: field key → column index, or `null` for *not fed*.
 *
 * A column index rather than a header string, because a sheet may repeat a header, may have
 * none at all, and may be remapped by hand — the position is the only thing that stays true
 * through all three.
 */
export type ImportMapping = Readonly<Record<string, number | null>>;

/**
 * What would happen to one row.
 *
 * `skip` and `error` are deliberately different words for two different facts. A **skip** is
 * the import working correctly — the row already exists, so a re-paste of a register is a
 * no-op rather than a second copy of everything. An **error** is this row failing a rule the
 * form would have failed too: an unknown assignee, a location outside the caller's scope, a
 * date that is not a date. Collapsing them would make a clean round trip look like 200
 * problems.
 */
export type ImportVerdict = "create" | "update" | "skip" | "error";

export interface ImportRow {
  /** Index into the grid's **data** rows — the header row, if any, is not one of them. */
  readonly index: number;
  readonly verdict: ImportVerdict;
  /** What this row would be, in the surface's own words. One line. */
  readonly summary: string;
  /** Where it would land. Empty where the surface has no such notion. */
  readonly where: string;
  /** Why it is skipped or refused, already translated. `null` for `create`/`update`. */
  readonly reason: string | null;
}

export interface ImportPreview {
  readonly rows: readonly ImportRow[];
  /**
   * One sentence above the table — a cap reached, a slice that excluded nodes, a request the
   * server refused outright. `null` when there is nothing to add.
   */
  readonly note: string | null;
}

export interface ImportOutcome {
  readonly written: number;
  /** Rows the write refused, by their preview index, with the server's own reason. */
  readonly failed: readonly { readonly index: number; readonly reason: string }[];
  readonly note: string | null;
}

/**
 * What one surface knows about importing into itself.
 *
 * `preview` and `commit` are handed the same three things, so the second cannot drift from the
 * first: whatever the person confirmed is re-derived from the grid they were shown rather than
 * from state the drawer carried between the two calls.
 */
export interface ImportAdapter {
  readonly fields: readonly ImportField[];
  readonly preview: (
    grid: readonly (readonly string[])[],
    mapping: ImportMapping,
  ) => Promise<ImportPreview>;
  /**
   * Write the rows the person kept. `keep` holds the indices of `ImportRow`s they did not
   * exclude; a row the preview called `skip` or `error` is never in it.
   */
  readonly commit: (
    grid: readonly (readonly string[])[],
    mapping: ImportMapping,
    keep: readonly number[],
  ) => Promise<ImportOutcome>;
}

/**
 * A header, as the matcher compares it.
 *
 * Accents are stripped because a Slovak studio's sheet says `Termín` and a colleague's export
 * says `Termin`, and treating those as different columns would be a distinction no person
 * makes. `NFD` + the combining-mark range is the whole of it — no transliteration table, which
 * would be a second place to keep in step with the languages we support.
 */
export function normalizeHeader(text: string): string {
  return text
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim();
}

/**
 * Every field's column, matched by header. Unmatched fields answer `null`.
 *
 * First match wins per field and a column feeds at most one field: a sheet with *Who* and
 * *Owner* in it maps the first and leaves the second for the person, rather than silently
 * deciding that the later column is the real one.
 */
export function autoMap(
  headers: readonly string[],
  fields: readonly ImportField[],
): ImportMapping {
  const normalized = headers.map(normalizeHeader);
  const taken = new Set<number>();
  const mapping: Record<string, number | null> = {};
  for (const field of fields) {
    const wanted = new Set(field.synonyms.map(normalizeHeader));
    // The field's own label counts as a synonym: a sheet produced by copying this very table
    // carries the translated header, and the round trip should not depend on somebody having
    // remembered to list it twice.
    wanted.add(normalizeHeader(field.label));
    const at = normalized.findIndex(
      (header, index) =>
        header !== "" && !taken.has(index) && wanted.has(header),
    );
    mapping[field.key] = at === -1 ? null : at;
    if (at !== -1) taken.add(at);
  }
  return mapping;
}

/**
 * Whether the grid's first line is headers rather than data.
 *
 * The test is *"does it name fields?"* and not *"does it look textual?"*: a register whose
 * first task is called `Due` would otherwise lose a row. Two or more cells matching a known
 * synonym is the threshold — one is a coincidence a real sheet produces often enough.
 */
export function looksLikeHeader(
  row: readonly string[] | undefined,
  fields: readonly ImportField[],
): boolean {
  if (row === undefined) return false;
  const known = new Set<string>();
  for (const field of fields) {
    for (const word of field.synonyms) known.add(normalizeHeader(word));
    known.add(normalizeHeader(field.label));
  }
  const hits = row.filter((cell) => known.has(normalizeHeader(cell))).length;
  return hits >= 2;
}

/**
 * The mapping a person last used on this surface, for a sheet shaped like this one.
 *
 * Per surface *and* per header signature: the studio keeps more than one sheet, and remembering
 * "column 3 is the due date" from the drawing list would be actively wrong when the task list
 * arrives with different columns. Storage is a convenience and never state anything depends on
 * — a private window, cleared site data or a browser that throws on the accessor all degrade to
 * *no memory*, which is the same behaviour as the first ever paste.
 */
const MEMORY_PREFIX = "kalqa.import-mapping.";

function signatureOf(headers: readonly string[]): string {
  return headers.map(normalizeHeader).join("|");
}

export function recallMapping(
  surface: string,
  headers: readonly string[],
): ImportMapping | null {
  try {
    const raw = window.localStorage.getItem(MEMORY_PREFIX + surface);
    if (raw === null) return null;
    const held: unknown = JSON.parse(raw);
    if (typeof held !== "object" || held === null) return null;
    const entry = (held as Record<string, unknown>)[signatureOf(headers)];
    if (typeof entry !== "object" || entry === null) return null;
    const mapping: Record<string, number | null> = {};
    for (const [key, value] of Object.entries(entry)) {
      mapping[key] = typeof value === "number" ? value : null;
    }
    return mapping;
  } catch {
    // A quota error, a disabled store, a value somebody hand-edited: all of them mean the same
    // thing here, which is that this paste gets auto-matching and no memory.
    return null;
  }
}

export function rememberMapping(
  surface: string,
  headers: readonly string[],
  mapping: ImportMapping,
): void {
  try {
    const key = MEMORY_PREFIX + surface;
    const raw = window.localStorage.getItem(key);
    const held: unknown = raw === null ? {} : JSON.parse(raw);
    const bag: Record<string, unknown> =
      typeof held === "object" && held !== null
        ? (held as Record<string, unknown>)
        : {};
    bag[signatureOf(headers)] = mapping;
    window.localStorage.setItem(key, JSON.stringify(bag));
  } catch {
    // Nothing to report and nothing to retry: the import works identically without memory.
  }
}

/** One cell of a row, through the mapping. `""` where the field is unmapped or short. */
export function cellOf(
  row: readonly string[] | undefined,
  mapping: ImportMapping,
  fieldKey: string,
): string {
  const at = mapping[fieldKey];
  if (row === undefined || at === undefined || at === null) return "";
  return (row[at] ?? "").trim();
}
